<template>
  <slice
    :id="block._key"
    class="slice--issues"
    @in="$emit('in')"
    @out="$emit('out')"
  >
    <div class="container">
      <div class="row-md row-hard justify-center">
        <div class="slice--issues__inner col-md-10">
          <portable-text
            v-if="upTitleFilled"
            :blocks="block.up_title"
            class="text-small text-center mb-1"
          />

          <portable-text
            v-if="titleFilled"
            :blocks="block.title"
            class="text-center mb-2 mb-md-4"
          />

          <div
            v-waypoint="{
              active: true,
              callback: onWaypoint,
              options: intersectionOptions,
            }"
            class="row-md issues-list justify-center"
          >
            <div
              v-for="(item, index) in block.issues"
              :key="item._key"
              class="issues-list__item col col-md-3 text-center"
            >
              <p
                v-if="item.percent"
                ref="percent"
                :data-percent="item.percent"
                class="mb-0 issues-list__percent"
              >
                <span>0%</span>
                <svg
                  class="issues-list__percent__circle-progress"
                  width="120"
                  height="120"
                  viewBox="0 0 120 120"
                >
                  <circle
                    class="progress-ring__circle"
                    stroke="var(--tertiary-color)"
                    stroke-width="2"
                    fill="transparent"
                    r="58"
                    cx="60"
                    cy="60"
                    :stroke-dasharray="`${circumference} ${circumference}`"
                    :stroke-dashoffset="getProgress(0)"
                  />
                </svg>
              </p>
              <p
                v-else
                class="mb-0 issues-list__percent issues-list__percent--number"
              >
                {{ index + 1 }}
              </p>

              <portable-text
                v-if="item.issue && item.issue.length > 0"
                :blocks="item.issue"
                class="text-md-big w-600"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </slice>
</template>

<script>
import Slice from '@/components/Slices/Slice'
import PortableText from '@/components/RichText/PortableText'

export default {
  name: 'BlockIssues',
  components: {
    Slice,
    PortableText,
  },
  props: {
    block: { type: Object, required: true },
  },
  data() {
    return {
      intersectionOptions: {
        root: null,
        rootMargin: '0px 0px 0px 0px',
        threshold: [0, 1], // [0.25, 0.75] if you want a 25% offset!
      },
    }
  },
  computed: {
    titleFilled() {
      return this.block.title && this.block.title.length > 0
    },
    upTitleFilled() {
      return this.block.up_title && this.block.up_title.length > 0
    },
    circumference() {
      return 58 * 2 * Math.PI
    },
  },
  methods: {
    getProgress(percent) {
      return this.circumference - (percent / 100) * this.circumference
    },
    async animate() {
      const { gsap } = await import('gsap' /* webpackChunkName: 'gsap' */)

      const percents = this.$refs.percent

      if (percents) {
        percents.forEach((p, index) => {
          const start = { val: 0 }
          gsap.to(start, {
            val: parseInt(p.dataset.percent),
            duration: 0.8,
            delay: index * 0.2,
            onUpdate: () => {
              p.children[0].innerHTML = `${Math.floor(start.val)}%`
              const circleProgress = this.getProgress(start.val)
              p.getElementsByClassName(
                'progress-ring__circle'
              )[0].style.strokeDashoffset = circleProgress
            },
          })
        })
      }
    },
    onWaypoint({ going }) {
      // going: in, out
      // direction: top, right, bottom, left
      if (going === this.$waypointMap.GOING_IN) {
        this.animate()
      }
    },
  },
}
</script>

<style lang="scss">
.slice--issues {
  flex-wrap: wrap;
  color: var(--tertiary-color);

  @include mq($until: tablet) {
    background: var(--secondary-color);
  }

  &__inner {
    padding: rem($spacing * 4) 0;

    @include mq($from: tablet) {
      background: var(--secondary-color);
      padding: rem($spacing * 6) rem($spacing * 4);
    }
  }

  .portable-text {
    --portable-text-color: var(--tertiary-color);
  }

  .issues-list {
    --grid-padding: #{rem($spacing * 2)};

    &__item {
      @include mq($until: tablet) {
        margin-bottom: rem($spacing * 2);

        &:last-child {
          margin-bottom: 0;
        }
      }
    }

    &__percent {
      --size: #{rem(76px)};

      @include mq($from: tablet) {
        --size: #{rem(90px)};
      }

      display: flex;
      align-items: center;
      justify-content: center;
      width: var(--size);
      height: var(--size);
      font-family: var(--secondary-font-family);
      font-size: rem($h4-fs);
      border: 1px solid var(--secondary-color-2);
      border-radius: 50%;
      margin: 0 auto rem($spacing * 3 / 5);
      position: relative;

      &__circle-progress {
        position: absolute;
        top: rem(-2px);
        left: rem(-2px);
        width: calc(100% + #{rem(4px)});
        height: calc(100% + #{rem(4px)});
        transform: rotate(-90deg);
        transform-origin: 50% 50%;
      }
    }
  }
}
</style>
